import React, { useEffect, useContext } from 'react';
import { Container, Grid, SimpleGrid, Alert, Box, Switch, Input, Divider, Title } from '@mantine/core';
import { IconAlertCircle, IconCalendar } from '@tabler/icons-react';
import { MetricsCard } from './components/MetricsCard';
import { SetupProgress } from './components/SetupProgress';
import { ActionItems } from './components/ActionItems';
import DateRangeFilter from 'components/DateRangeFilter';
import SEO from 'components/SEO';
import { useDashboard } from 'context/DashboardContext';
import AuthContext from 'context/AuthContext';
import { MAX_FREE_STAFF_MEMBERS } from 'constants/settings';
import { MAX_FREE_CARD_LENGTH } from 'constants/settings';
import { AccessLevel } from 'interfaces/GetUserResult';
import dayjs from 'dayjs';
import { IQuickRange } from 'components/DateRangeFilter/IQuickRange';
import { useSearchParams } from 'react-router-dom';

type ChangeType = 'increase' | 'decrease';

const dashboardQuickRanges: IQuickRange[] = [
  { label: 'Last 12 months', value: 12, unit: 'months', customValue: '12mo' },
  { label: 'Last 3 months', value: 3, unit: 'months', customValue: '3mo' },
  { label: 'Last 30 days', value: 30, unit: 'days', customValue: '30d' },
  { label: 'Last 7 days', value: 7, unit: 'days', customValue: '7d' },
];

const Dashboard = () => {
  const { primaryStats, comparisonStats, loading, error, fetchStats, setDateRange } = useDashboard();
  const { data: { company, account } } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentRange, setCurrentRange] = React.useState<{ from?: Date; to?: Date }>();
  const [comparisonRange, setComparisonRange] = React.useState<{ from?: Date; to?: Date } | null>(null);
  const [customComparison, setCustomComparison] = React.useState(
    searchParams.get('vs_custom_comparison') === 'true'
  );

  const handleCustomComparisonToggle = (checked: boolean) => {
    const newParams = new URLSearchParams(searchParams);
    if (checked) {
      newParams.set('vs_custom_comparison', 'true');
      if (currentRange?.from && currentRange?.to) {
        const duration = currentRange.to.getTime() - currentRange.from.getTime();
        const prevFrom = new Date(currentRange.from.getTime() - duration - 86400000);
        const prevTo = new Date(currentRange.to.getTime() - duration - 86400000);
        const prevRange = { from: prevFrom, to: prevTo };
        setComparisonRange(prevRange);
        // Immediately apply filter to set vs_ params
        fetchStats(prevRange, 'comparison');
        // Optionally, if you want to update the URL params directly:
        newParams.set('vs_start_date', prevFrom.toISOString());
        newParams.set('vs_end_date', prevTo.toISOString());
        newParams.delete('vs_date');
      }
    } else {
      newParams.delete('vs_custom_comparison');
      newParams.delete('vs_date');
      newParams.delete('vs_start_date');
      newParams.delete('vs_end_date');
      setComparisonRange(null);
    }
    setSearchParams(newParams);
  };

  useEffect(() => {
    const urlValue = searchParams.get('vs_custom_comparison') === 'true';
    setCustomComparison(urlValue);
    if (!urlValue) {
      setComparisonRange(null);
    }
    // Intentionally excluding setComparisonRange from dependencies as it's a stable useState setter
    // and this effect should only run when the comparison URL parameter changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('vs_custom_comparison')]);

  useEffect(() => {
    if (currentRange?.from && currentRange?.to) {
      fetchStats(currentRange, 'primary');
    }
  }, [currentRange, fetchStats]);

  useEffect(() => {
    if (customComparison && comparisonRange?.from && comparisonRange?.to) {
      fetchStats(comparisonRange, 'comparison');
    } else if (currentRange?.from && currentRange?.to) {
      const duration = currentRange.to.getTime() - currentRange.from.getTime();
      const prevFrom = new Date(currentRange.from.getTime() - duration - 86400000);
      const prevTo = new Date(currentRange.to.getTime() - duration - 86400000);
      fetchStats({ from: prevFrom, to: prevTo }, 'comparison');
    }
  }, [customComparison, comparisonRange, currentRange, fetchStats]);

  // Helper to format date range
  const formatRange = (range?: { from?: Date; to?: Date }) => {
    if (!range?.from || !range?.to) return '';
    return `${dayjs(range.from).format('MMM D, YYYY')} - ${dayjs(range.to).format('MMM D, YYYY')}`;
  };

  // Calculate previous range for summary if no custom comparison
  let effectiveComparisonRange: { from?: Date; to?: Date } | null = null;
  if (customComparison && comparisonRange?.from && comparisonRange?.to) {
    effectiveComparisonRange = comparisonRange;
  } else if (currentRange?.from && currentRange?.to) {
    const duration = currentRange.to.getTime() - currentRange.from.getTime();
    effectiveComparisonRange = {
      from: new Date(currentRange.from.getTime() - duration - 86400000),
      to: new Date(currentRange.to.getTime() - duration - 86400000),
    };
  }

  // Calculate total characters in business information
  const getTotalChars = () => {
    const cards = company?.cards || [];
    return cards.reduce((total: number, card: { title?: string; body?: string }) => {
      const titleLength = card.title?.length || 0;
      const bodyLength = card.body?.length || 0;
      return total + titleLength + bodyLength;
    }, 0);
  };

  const getChangePercentage = (current: number, previous: number) => {
    if (previous === 0) {
      // Return a special high value to indicate "new" entries
      return current > 0 ? 999 : 0;
    }
    if (isNaN(current) || isNaN(previous)) return 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const getChangeProps = (change: number) => ({
    value: Math.abs(change),
    type: (change >= 0 ? 'increase' : 'decrease') as ChangeType
  });

  const totalCallsChange = getChangePercentage(primaryStats?.totalCalls ?? 0, comparisonStats?.totalCalls ?? 0);
  const performanceScoreChange = getChangePercentage(primaryStats?.performanceScore ?? 0, comparisonStats?.performanceScore ?? 0);

  const staffCount = company?.users?.length || 0;
  const totalChars = getTotalChars();

  // Show action items for free users
  const showActionItems = account?.access_level === AccessLevel.FREE;

  // Helper to determine if comparison range conflicts with primary range
  const isComparisonRangeConflict = (primary: { from?: Date; to?: Date }, comparison: { from?: Date; to?: Date }) => {
    if (!primary.from || !primary.to || !comparison.from || !comparison.to) return false;
    // Conflict if comparison range overlaps or is not strictly before primary range
    return comparison.to >= primary.from;
  };

  const getComparisonPeriodLabel = (primary: { from?: Date; to?: Date } | null, comparison: { from?: Date; to?: Date } | null) => {
    if (!primary || !comparison || !primary.from || !primary.to || !comparison.from || !comparison.to) {
      return 'prev. period';
    }
    
    // Calculate the duration in days
    const primaryDurationMs = primary.to.getTime() - primary.from.getTime();
    const primaryDays = Math.round(primaryDurationMs / (1000 * 60 * 60 * 24));
    
    // For standard quick ranges (7d, 30d, etc.)
    if (primaryDays === 7) return 'prev. 7 days';
    if (primaryDays === 30) return 'prev. 30 days';
    if (primaryDays >= 28 && primaryDays <= 31) return 'prev. month';
    if (primaryDays >= 85 && primaryDays <= 95) return 'prev. 3 months';
    if (primaryDays >= 350 && primaryDays <= 370) return 'prev. year';
    
    // For custom date ranges, provide a succinct summary
    return `prev. ${primaryDays} days`;
  };

  return (
    <>
      <SEO 
        title="Dashboard | Nucleus AI"
        description="View your AI receptionist dashboard with call analytics, performance metrics, and setup progress."
      />
      <Box 
        style={{ 
          height: 'calc(100vh - 64px)', // Account for header height
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        <Box style={{ padding: '0' }}>
          <Container fluid p={0} mb={{ base: 'sm', md: 'md' }}>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              alignItems: 'center',
              gap: '16px',
              marginBottom: '16px'
            }}>
              {/* Primary date filter */}
              <div style={{ width: '220px' }}>
                  <DateRangeFilter
                    onApply={(range: { from?: Date; to?: Date }) => {
                      setDateRange(range);
                      setCurrentRange(range);
                      if (customComparison && comparisonRange && isComparisonRangeConflict(range, comparisonRange)) {
                        const duration = range.to!.getTime() - range.from!.getTime();
                        const prevFrom = new Date(range.from!.getTime() - duration - 86400000);
                        const prevTo = new Date(range.to!.getTime() - duration - 86400000);
                        const prevRange = { from: prevFrom, to: prevTo };
                        setComparisonRange(prevRange);
                        fetchStats(prevRange, 'comparison');
                        const newParams = new URLSearchParams(searchParams);
                        newParams.set('vs_start_date', prevFrom.toISOString());
                        newParams.set('vs_end_date', prevTo.toISOString());
                        newParams.delete('vs_date');
                        setSearchParams(newParams);
                      }
                    }}
                    quickRanges={dashboardQuickRanges}
                    defaultCustomValue="30d"
                  />
              </div>
              
              {/* vs. label */}
              <div style={{ padding: '0', margin: '0 4px' }}>
                <span
                  style={{
                    fontWeight: 700,
                    color: 'var(--mantine-color-gray-5, #adb5bd)',
                    fontSize: 20,
                    whiteSpace: 'nowrap',
                  }}
                >
                  vs.
                </span>
              </div>
              
              {/* Secondary date filter */}
              <div style={{ width: '220px' }}>
                  {customComparison ? (
                    <DateRangeFilter
                      onApply={(range: { from?: Date; to?: Date }) => setComparisonRange(range)}
                      quickRanges={dashboardQuickRanges}
                      hideQuickRanges={true}
                      maxDate={currentRange?.from ? new Date(currentRange.from.getTime() - 86400000) : undefined}
                      searchParamsPrefix="vs_"
                      value={comparisonRange || undefined}
                    />
                  ) : (
                  <Input
                    radius="0.375rem"
                    disabled
                    value={formatRange(effectiveComparisonRange ?? undefined)}
                    rightSection={<IconCalendar stroke={1} />}
                    name="comparison-disabled"
                    w="100%"
                    styles={{
                      input: {
                        height: '2.75rem',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        background: 'var(--mantine-color-gray-1, #f8f9fa)',
                        border: '1px solid var(--mantine-color-gray-3, #e9ecef)',
                        borderRadius: 6,
                        fontWeight: 500,
                        fontSize: 14,
                        color: 'var(--mantine-color-gray-8, #1a1a1a)',
                      },
                      section: {
                        width: '2.5rem',
                      },
                      }}
                  />
                  )}
              </div>
              
              {/* Custom comparison switch */}
              <div style={{ width: '220px' }}>
              <Switch
                  mt={0}
                label="Custom comparison"
                checked={customComparison}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleCustomComparisonToggle(event.currentTarget.checked);
                }}
              />
              </div>
            </div>
          </Container>
        </Box>

        <Box 
          style={{ 
            flex: 1,
            overflow: 'auto',
            padding: '0',
            paddingBottom: 'calc(var(--mantine-spacing-md) * 3)'
          }}
        >
          <Container fluid p={0} mb={{ base: 'sm', md: 'md' }}>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="md">
            <MetricsCard
              label="Total calls"
              value={primaryStats?.totalCalls ?? 0}
              previousValue={comparisonStats?.totalCalls ?? 0}
              change={getChangeProps(totalCallsChange)}
                comparisonLabel={customComparison ? 'custom period' : getComparisonPeriodLabel(currentRange ?? null, effectiveComparisonRange ?? null)}
                loading={loading}
                tooltip="The total number of calls received during the selected time period, including completed, missed, and ongoing calls."
            />
            <MetricsCard
                label="Calls resolved"
                labelDescriptor="by Reception"
                value={primaryStats?.totalCalls ? Math.round((primaryStats?.completedCalls / primaryStats?.totalCalls) * 100) : 0}
                previousValue={comparisonStats?.totalCalls ? Math.round((comparisonStats?.completedCalls / comparisonStats?.totalCalls) * 100) : 0}
                change={getChangeProps(getChangePercentage(
                  primaryStats?.totalCalls ? (primaryStats?.completedCalls / primaryStats?.totalCalls) * 100 : 0,
                  comparisonStats?.totalCalls ? (comparisonStats?.completedCalls / comparisonStats?.totalCalls) * 100 : 0
                ))}
                suffix="%"
                comparisonLabel={customComparison ? 'custom period' : getComparisonPeriodLabel(currentRange ?? null, effectiveComparisonRange ?? null)}
                loading={loading}
                tooltip="The percentage of calls that were resolved by the AI reception that required no additional follow up."
            />
            <MetricsCard
              label="Overall performance score"
              value={primaryStats?.performanceScore ?? 0}
              previousValue={comparisonStats?.performanceScore ?? 0}
              change={getChangeProps(performanceScoreChange)}
              suffix="/100"
                comparisonLabel={customComparison ? 'custom period' : getComparisonPeriodLabel(currentRange ?? null, effectiveComparisonRange ?? null)}
                loading={loading}
                tooltip="A comprehensive score out of 100 that evaluates your AI receptionist's performance based on call handling, response quality, and customer satisfaction metrics."
            />
            <MetricsCard
              label="Active calls"
              value={primaryStats?.activeCalls ?? 0}
                comparisonLabel={customComparison ? 'custom period' : getComparisonPeriodLabel(currentRange ?? null, effectiveComparisonRange ?? null)}
                loading={loading}
                tooltip="The number of calls currently in progress. This metric shows real-time activity levels."
            />
          </SimpleGrid>
          </Container>

          <Container fluid p={0} mt="xl">
            <Divider mb="lg" />
            <Title order={3} mb="md">Setup Progress & Recommendations</Title>
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, md: showActionItems ? 8 : 12 }}>
              <SetupProgress />
            </Grid.Col>
            {showActionItems && (
              <Grid.Col span={{ base: 12, md: 4 }}>
                <ActionItems
                  teamMemberCount={staffCount}
                  teamMemberLimit={MAX_FREE_STAFF_MEMBERS}
                  characterCount={totalChars}
                  characterLimit={MAX_FREE_CARD_LENGTH}
                />
              </Grid.Col>
            )}
          </Grid>
          </Container>
        </Box>

        {error && (
          <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red" mb="md">
            {error}
          </Alert>
        )}
      </Box>
    </>
  );
};

export default Dashboard;
